import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  type DocumentData,
  type DocumentSnapshot,
  type Firestore,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import { logAuditEvent } from './auditLedgerService'
import { firestoreDb } from './firebase'
import { readChecklistCounterSteps } from './checklistCounterTransaction'
import { materializeTemplate } from './templateMaterializers'
import { PremiumTemplateRequiredError, isPremiumTier } from './subscriptionGuard'
import {
  isTemplateCategory,
  TEMPLATE_CATEGORIES,
  isExploreTemplateStatus,
  type ExploreTemplateDoc,
  type ExploreTemplateStatus,
  type SavedTemplateRecord,
  type TemplateCategory,
  type TemplatePreviewData,
  type TemplateRegistryEntry,
  type WorkspaceTemplateClone,
} from '../types/templates'
import { createStripboardRecordDefaults } from '../utils/stripboardDefaults'
import { bootstrapChecklistsOnBoard } from './checklistWriter'
import { BUNDLED_EXPLORE_TEMPLATES, getBundledExploreTemplate } from '../data/explore_templates'

export const SAVED_TEMPLATES_COLLECTION = 'saved_templates'
export const WORKSPACE_TEMPLATES_COLLECTION = 'templates'
export const EXPLORE_TEMPLATES_REGISTRY_COLLECTION = 'explore_templates_registry'

export type TemplateImportActor = { id: string; name?: string | null }

export class MaterializeFailedError extends Error {
  readonly category: string
  readonly templateId: string
  readonly cause: unknown

  constructor(category: string, templateId: string, cause: unknown) {
    super(`Materialization failed for template "${templateId}" in category "${category}"`)
    this.name = 'MaterializeFailedError'
    this.category = category
    this.templateId = templateId
    this.cause = cause
  }
}

let didLogTemplateRegistryFallback = false

const logTemplateRegistryFallbackOnce = (error: Error) => {
  if (didLogTemplateRegistryFallback) return
  didLogTemplateRegistryFallback = true
  console.info('[explore] Firestore template registry unavailable; using bundled templates for this session.', error)
}

const boardRootId = (workspaceId: string, board: 'VISIONBOARD' | 'DASHBOARD' | 'ARCHIVE' | 'BIN') => `board-root:${workspaceId}:${board}`

const emptyPreview: TemplatePreviewData = {
  title: 'Untitled template',
  summary: 'No preview data is available.',
  metrics: [],
  sections: [],
}

const isRemovedTemplateCategory = (category: unknown) => category === 'Meetings'
const keepsExploreTemplate = (entry: TemplateRegistryEntry) => (entry.category as string) !== 'Meetings'

const normalizePreviewData = (value: unknown, fallbackTitle: string): TemplatePreviewData => {
  if (!value || typeof value !== 'object') return { ...emptyPreview, title: fallbackTitle }
  const data = value as Record<string, unknown>
  return {
    title: typeof data.title === 'string' ? data.title : fallbackTitle,
    summary: typeof data.summary === 'string' ? data.summary : '',
    metrics: Array.isArray(data.metrics)
      ? data.metrics.flatMap((entry) => {
        if (!entry || typeof entry !== 'object') return []
        const metric = entry as Record<string, unknown>
        if (typeof metric.label !== 'string') return []
        return [{ label: metric.label, value: typeof metric.value === 'number' || typeof metric.value === 'string' ? metric.value : String(metric.value ?? '') }]
      })
      : [],
    sections: Array.isArray(data.sections)
      ? data.sections.flatMap((entry) => {
        if (!entry || typeof entry !== 'object') return []
        const section = entry as Record<string, unknown>
        if (typeof section.title !== 'string') return []
        return [{ title: section.title, items: Array.isArray(section.items) ? section.items.map(String) : [] }]
      })
      : [],
    payload: data.payload && typeof data.payload === 'object' ? data.payload as Record<string, unknown> : {},
  }
}

const stableStringify = (value: unknown): string => {
  if (value === null || typeof value !== 'object') return JSON.stringify(value)
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`
  const record = value as Record<string, unknown>
  return `{${Object.keys(record).sort().map((key) => `${JSON.stringify(key)}:${stableStringify(record[key])}`).join(',')}}`
}

export const computeTemplatePreviewDataHash = (previewData: TemplatePreviewData) => stableStringify(previewData)

export const templateDocToRegistryEntry = (
  snapshot: DocumentSnapshot<DocumentData>,
  options: { requirePublished?: boolean } = {},
): TemplateRegistryEntry | null => {
  if (!snapshot.exists()) return null
  const data = snapshot.data()
  const status = isExploreTemplateStatus(data.status) ? data.status : 'DRAFT'
  if (options.requirePublished && status !== 'PUBLISHED') return null
  if (isRemovedTemplateCategory(data.category)) return null
  const category = isTemplateCategory(data.category) ? data.category : null
  if (!category || category === 'Workspaces') return null
  const title = typeof data.title === 'string' ? data.title : 'Untitled template'
  return {
    id: typeof data.id === 'string' ? data.id : snapshot.id,
    title,
    description: typeof data.description === 'string' ? data.description : '',
    category,
    tags: Array.isArray(data.tags) ? data.tags.map(String).filter(Boolean) : [],
    registryVersion: Number.isInteger(data.registryVersion) ? data.registryVersion : 1,
    previewData: normalizePreviewData(data.previewData, title),
    createdAt: data.createdAt ?? null,
    updatedAt: data.updatedAt ?? null,
    featured: data.featured === true,
    tier: data.tier === 'PREMIUM' ? 'PREMIUM' : 'FREE',
  }
}

export const templateDocToOwnerEntry = (
  snapshot: DocumentSnapshot<DocumentData>,
): ExploreTemplateDoc | null => {
  if (!snapshot.exists()) return null
  const data = snapshot.data()
  const registry = templateDocToRegistryEntry(snapshot)
  if (!registry) return null
  return {
    ...registry,
    status: isExploreTemplateStatus(data.status) ? data.status : 'DRAFT',
    overridesBundledId: typeof data.overridesBundledId === 'string' ? data.overridesBundledId : null,
    createdByUid: typeof data.createdByUid === 'string' ? data.createdByUid : '',
    updatedByUid: typeof data.updatedByUid === 'string' ? data.updatedByUid : '',
    publishedAt: data.publishedAt ?? null,
    previewDataHash: typeof data.previewDataHash === 'string' ? data.previewDataHash : undefined,
  }
}

const registryEntriesFromSnapshot = (
  docs: Array<DocumentSnapshot<DocumentData>>,
  category: TemplateCategory | null,
) => docs
  .map((snapshot) => templateDocToRegistryEntry(snapshot, { requirePublished: true }))
  .filter((entry): entry is TemplateRegistryEntry => Boolean(entry))
  .filter(keepsExploreTemplate)
  .filter((entry) => category ? entry.category === category : entry.category !== 'Workspaces')

export const getEffectiveExploreTemplate = async (
  templateId: string,
  db: Firestore = firestoreDb,
) => {
  if (!templateId) return null
  const snapshot = await getDoc(doc(db, EXPLORE_TEMPLATES_REGISTRY_COLLECTION, templateId))
  return templateDocToRegistryEntry(snapshot, { requirePublished: true }) ?? getBundledExploreTemplate(templateId)
}


const filterRegistryTemplates = (templates: TemplateRegistryEntry[], category: TemplateCategory | null) =>
  templates
    .filter(keepsExploreTemplate)
    .filter((template) => category ? template.category === category : template.category !== 'Workspaces')

const compareRegistryTemplates = (left: TemplateRegistryEntry, right: TemplateRegistryEntry) => {
  const categoryDelta = TEMPLATE_CATEGORIES.indexOf(left.category) - TEMPLATE_CATEGORIES.indexOf(right.category)
  if (categoryDelta !== 0) return categoryDelta
  // Featured pinned above non-featured, within the same category.
  const featuredDelta = (right.featured ? 1 : 0) - (left.featured ? 1 : 0)
  if (featuredDelta !== 0) return featuredDelta
  return left.title.localeCompare(right.title)
}

export const mergeTemplateRegistryEntries = (
  firestoreTemplates: TemplateRegistryEntry[],
  category: TemplateCategory | null,
) => {
  const merged = new Map<string, TemplateRegistryEntry>()
  for (const template of filterRegistryTemplates(BUNDLED_EXPLORE_TEMPLATES, category)) merged.set(template.id, template)
  for (const template of filterRegistryTemplates(firestoreTemplates, category)) merged.set(template.id, template)
  return Array.from(merged.values()).sort(compareRegistryTemplates)
}

const getTemplateGroups = (previewData: TemplatePreviewData) => {
  const payload = previewData.payload
  if (!payload || !Array.isArray(payload.groups)) return []
  return payload.groups.flatMap((group) => {
    if (!group || typeof group !== 'object') return []
    const data = group as Record<string, unknown>
    if (typeof data.title !== 'string') return []
    return [{
      title: data.title,
      items: Array.isArray(data.items) ? data.items.map(String).filter(Boolean) : [],
    }]
  })
}

const buildChecklistTemplateDescription = (registry: TemplateRegistryEntry) => {
  const groups = getTemplateGroups(registry.previewData)
  const lines = [
    registry.description || registry.previewData.summary,
    '',
    ...groups.flatMap((group) => [
      group.title,
      ...group.items.map((item) => `- ${item}`),
      '',
    ]),
  ].map((line) => line.trimEnd())
  return lines.join('\n').trim()
}

const buildChecklistTemplateCriteria = (registry: TemplateRegistryEntry) => {
  const payload = registry.previewData.payload ?? {}
  const proofGates = Array.isArray(payload.proofGates) ? payload.proofGates.map(String).filter(Boolean) : []
  const ownerRoles = Array.isArray(payload.ownerRoles) ? payload.ownerRoles.map(String).filter(Boolean) : []
  const groups = getTemplateGroups(registry.previewData)
  const allItems = groups.flatMap((group) => group.items)
  const lines = [
    'Complete every template section before marking this checklist done.',
    allItems.length ? `Checklist scope: ${allItems.join('; ')}.` : '',
    proofGates.length ? `Proof gates: ${proofGates.join('; ')}.` : '',
    ownerRoles.length ? `Suggested owners: ${ownerRoles.join('; ')}.` : '',
  ].filter(Boolean)
  return lines.join('\n')
}

const savedFromSnapshot = (snapshot: QueryDocumentSnapshot<DocumentData>): SavedTemplateRecord | null => {
  const data = snapshot.data()
  if (isRemovedTemplateCategory(data.category)) return null
  return {
    id: snapshot.id,
    workspaceId: typeof data.workspaceId === 'string' ? data.workspaceId : '',
    templateId: typeof data.templateId === 'string' ? data.templateId : snapshot.id,
    category: isTemplateCategory(data.category) ? data.category : 'Workspaces',
    title: typeof data.title === 'string' ? data.title : 'Untitled template',
    registryVersion: Number.isInteger(data.registryVersion) ? data.registryVersion : 1,
    sourceRegistryVersion: Number.isInteger(data.sourceRegistryVersion) ? data.sourceRegistryVersion : Number.isInteger(data.registryVersion) ? data.registryVersion : 1,
    importedBy: typeof data.importedBy === 'string' ? data.importedBy : '',
    importedByName: typeof data.importedByName === 'string' ? data.importedByName : null,
    importedAt: data.importedAt ?? null,
    updatedAt: data.updatedAt ?? null,
  }
}

const cloneFromSnapshot = (snapshot: QueryDocumentSnapshot<DocumentData>): WorkspaceTemplateClone | null => {
  const data = snapshot.data()
  if (isRemovedTemplateCategory(data.category)) return null
  const title = typeof data.title === 'string' ? data.title : 'Untitled template'
  const category = isTemplateCategory(data.category) ? data.category : 'Workspaces'
  const registryVersion = Number.isInteger(data.registryVersion) ? data.registryVersion : 1
  const sourceRegistryVersion = Number.isInteger(data.sourceRegistryVersion) ? data.sourceRegistryVersion : registryVersion
  return {
    id: snapshot.id,
    workspaceId: typeof data.workspaceId === 'string' ? data.workspaceId : '',
    templateId: typeof data.templateId === 'string' ? data.templateId : snapshot.id,
    sourceTemplateId: typeof data.sourceTemplateId === 'string' ? data.sourceTemplateId : snapshot.id,
    category,
    title,
    registryVersion,
    sourceRegistryVersion,
    importedBy: typeof data.importedBy === 'string' ? data.importedBy : '',
    importedByName: typeof data.importedByName === 'string' ? data.importedByName : null,
    importedAt: data.importedAt ?? null,
    updatedAt: data.updatedAt ?? null,
    sourcePreviewData: normalizePreviewData(data.sourcePreviewData, title),
    previewData: normalizePreviewData(data.previewData, title),
    isModified: data.isModified === true || JSON.stringify(data.previewData ?? null) !== JSON.stringify(data.sourcePreviewData ?? null),
    modifiedAt: data.modifiedAt ?? null,
  }
}

export const subscribeTemplateRegistry = (
  category: TemplateCategory | null,
  onChange: (templates: TemplateRegistryEntry[]) => void,
  onError?: (error: Error) => void,
  db: Firestore = firestoreDb,
) => {
  const constraints = category && category !== 'Workspaces'
    ? [where('status', '==', 'PUBLISHED'), where('category', '==', category), orderBy('title')]
    : [where('status', '==', 'PUBLISHED'), orderBy('title')]
  return onSnapshot(query(collection(db, EXPLORE_TEMPLATES_REGISTRY_COLLECTION), ...constraints), (snapshot) => {
    onChange(mergeTemplateRegistryEntries(registryEntriesFromSnapshot(snapshot.docs, category), category))
  }, (error) => {
    logTemplateRegistryFallbackOnce(error)
    onChange(mergeTemplateRegistryEntries([], category))
    onError?.(error)
  })
}

export const subscribeOwnerTemplateRegistry = (
  onChange: (templates: ExploreTemplateDoc[]) => void,
  onError?: (error: Error) => void,
  db: Firestore = firestoreDb,
) => onSnapshot(
  query(collection(db, EXPLORE_TEMPLATES_REGISTRY_COLLECTION), orderBy('title')),
  (snapshot) => {
    onChange(snapshot.docs.map(templateDocToOwnerEntry).filter((entry): entry is ExploreTemplateDoc => Boolean(entry)))
  },
  (error) => onError?.(error),
)

export const saveOwnerExploreTemplate = async (
  template: ExploreTemplateDoc,
  actorUid: string,
  db: Firestore = firestoreDb,
) => {
  const existing = await getDoc(doc(db, EXPLORE_TEMPLATES_REGISTRY_COLLECTION, template.id))
  const existingDoc = templateDocToOwnerEntry(existing)
  const nextHash = computeTemplatePreviewDataHash(template.previewData)
  const changedPayload = existingDoc ? existingDoc.previewDataHash !== nextHash : true
  const nextVersion = changedPayload ? Math.max(1, template.registryVersion + (existingDoc ? 1 : 0)) : template.registryVersion
  const publishedAt = template.status === 'PUBLISHED'
    ? existingDoc?.publishedAt ?? serverTimestamp()
    : null
  await setDoc(doc(db, EXPLORE_TEMPLATES_REGISTRY_COLLECTION, template.id), {
    ...template,
    id: template.id,
    registryVersion: nextVersion,
    previewDataHash: nextHash,
    createdByUid: existingDoc?.createdByUid || template.createdByUid || actorUid,
    updatedByUid: actorUid,
    createdAt: existingDoc?.createdAt ?? template.createdAt ?? serverTimestamp(),
    updatedAt: serverTimestamp(),
    publishedAt,
  }, { merge: false })
}

export const updateOwnerExploreTemplateStatus = async (
  templateId: string,
  status: ExploreTemplateStatus,
  actorUid: string,
  db: Firestore = firestoreDb,
) => {
  await updateDoc(doc(db, EXPLORE_TEMPLATES_REGISTRY_COLLECTION, templateId), {
    status,
    updatedByUid: actorUid,
    updatedAt: serverTimestamp(),
    ...(status === 'PUBLISHED' ? { publishedAt: serverTimestamp() } : {}),
  })
}

export const deleteOwnerExploreTemplate = (
  templateId: string,
  db: Firestore = firestoreDb,
) => deleteDoc(doc(db, EXPLORE_TEMPLATES_REGISTRY_COLLECTION, templateId))

export const subscribeSavedTemplates = (
  workspaceId: string | null,
  onChange: (templates: SavedTemplateRecord[]) => void,
  onError?: (error: Error) => void,
  db: Firestore = firestoreDb,
) => {
  if (!workspaceId) {
    onChange([])
    return () => undefined
  }
  return onSnapshot(collection(db, 'workspaces', workspaceId, SAVED_TEMPLATES_COLLECTION), (snapshot) => {
    onChange(snapshot.docs.map(savedFromSnapshot).filter((template): template is SavedTemplateRecord => Boolean(template)))
  }, (error) => onError?.(error))
}

export const subscribeWorkspaceTemplateClones = (
  workspaceId: string | null,
  onChange: (templates: WorkspaceTemplateClone[]) => void,
  onError?: (error: Error) => void,
  db: Firestore = firestoreDb,
) => {
  if (!workspaceId) {
    onChange([])
    return () => undefined
  }
  return onSnapshot(collection(db, 'workspaces', workspaceId, WORKSPACE_TEMPLATES_COLLECTION), (snapshot) => {
    onChange(snapshot.docs.map(cloneFromSnapshot).filter((template): template is WorkspaceTemplateClone => Boolean(template)))
  }, (error) => onError?.(error))
}

export const importTemplate = async (
  workspaceId: string,
  templateId: string,
  actor: TemplateImportActor,
  plan: unknown = null,
  db: Firestore = firestoreDb,
  workspace?: unknown,
) => {
  if (!workspaceId) throw new Error('Workspace is required.')
  if (!templateId) throw new Error('Template is required.')
  if (!actor.id) throw new Error('Signed-in user is required.')

  const effectiveForGate = await getEffectiveExploreTemplate(templateId, db)
  if (effectiveForGate?.tier === 'PREMIUM' && !isPremiumTier(plan, workspace)) {
    throw new PremiumTemplateRequiredError(templateId, plan)
  }

  let importedRegistry: TemplateRegistryEntry | null = null
  await runTransaction(db, async (transaction) => {
    const workspaceRef = doc(db, 'workspaces', workspaceId)
    const savedRef = doc(db, 'workspaces', workspaceId, SAVED_TEMPLATES_COLLECTION, templateId)
    const cloneRef = doc(db, 'workspaces', workspaceId, WORKSPACE_TEMPLATES_COLLECTION, templateId)
    const registryRef = doc(db, EXPLORE_TEMPLATES_REGISTRY_COLLECTION, templateId)
    const [workspaceSnap, savedSnap, cloneSnap, registrySnap] = await Promise.all([
      transaction.get(workspaceRef),
      transaction.get(savedRef),
      transaction.get(cloneRef),
      transaction.get(registryRef),
    ])
    const registry = templateDocToRegistryEntry(registrySnap, { requirePublished: true }) ?? getBundledExploreTemplate(templateId)
    if (!registry) throw new Error('Template is no longer available.')
    importedRegistry = registry
    const currentClone = cloneSnap.exists() ? cloneFromSnapshot(cloneSnap as QueryDocumentSnapshot<DocumentData>) : null
    if (savedSnap.exists() && currentClone && currentClone.registryVersion === registry.registryVersion && !currentClone.isModified) {
      importedRegistry = null
      return
    }

    const common = {
      workspaceId,
      templateId,
      sourceTemplateId: registry.id,
      category: registry.category,
      title: registry.title,
      registryVersion: registry.registryVersion,
      sourceRegistryVersion: registry.registryVersion,
      importedBy: actor.id,
      importedByName: actor.name ?? null,
      updatedAt: serverTimestamp(),
    }
    transaction.set(savedRef, {
      ...common,
      importedAt: currentClone?.importedAt ?? serverTimestamp(),
    }, { merge: true })
    transaction.set(cloneRef, {
      ...common,
      description: registry.description,
      tags: registry.tags,
      sourcePreviewData: registry.previewData,
      previewData: registry.previewData,
      isModified: false,
      modifiedAt: null,
      importedAt: currentClone?.importedAt ?? serverTimestamp(),
    }, { merge: true })
    logAuditEvent(db, transaction, workspaceSnap.data(), {
      workspaceId,
      actorId: actor.id,
      actorName: actor.name ?? null,
      action: currentClone ? 'TEMPLATE_IMPORT_REFRESHED' : 'TEMPLATE_IMPORTED',
      activityType: currentClone ? 'UPDATE' : 'CREATE',
      nodeType: 'TEMPLATE',
      targetId: templateId,
      targetTitle: registry.title,
      summary: `${currentClone ? 'Refreshed' : 'Imported'} template: ${registry.title}`,
      payload: {
        category: registry.category,
        registryVersion: registry.registryVersion,
        sourceTemplateId: registry.id,
      },
    })
  })

  // Best-effort materialization into the target surface's own collection so
  // the imported template is a live, editable entity there (Form / Design /
  // Automation / Meeting). Failure here doesn't reverse the clone, but note
  // that addDoc ids are not deterministic across all materializers.
  if (importedRegistry) {
    const target: TemplateRegistryEntry = importedRegistry
    try {
      await materializeTemplate(workspaceId, target, actor)
    } catch (error) {
      console.warn('[explore] materialize failed', target.category, target.id, error)
      throw new MaterializeFailedError(target.category, target.id, error)
    }
  }
}

export const copyChecklistTemplateToVisionboard = async (
  workspaceId: string,
  templateId: string,
  actor: TemplateImportActor,
  plan: unknown = null,
  db: Firestore = firestoreDb,
  workspace?: unknown,
) => {
  if (!workspaceId) throw new Error('Workspace is required.')
  if (!templateId) throw new Error('Template is required.')
  if (!actor.id) throw new Error('Signed-in user is required.')

  const effectiveForGate = await getEffectiveExploreTemplate(templateId, db)
  if (effectiveForGate?.tier === 'PREMIUM' && !isPremiumTier(plan, workspace)) {
    throw new PremiumTemplateRequiredError(templateId, plan)
  }

  let copiedId = ''
  await runTransaction(db, async (transaction) => {
    const workspaceRef = doc(db, 'workspaces', workspaceId)
    const checklistRef = doc(collection(db, 'checklists'))
    const registryRef = doc(db, EXPLORE_TEMPLATES_REGISTRY_COLLECTION, templateId)
    const parentContainerId = boardRootId(workspaceId, 'VISIONBOARD')
    const [workspaceSnap, registrySnap, stepResult] = await Promise.all([
      transaction.get(workspaceRef),
      transaction.get(registryRef),
      readChecklistCounterSteps(transaction, db, {
        workspaceId,
        board: 'VISIONBOARD',
        parentContainerId,
        checklistId: checklistRef.id,
        nodeKind: 'CHECKLIST',
      }),
    ])
    const registry = templateDocToRegistryEntry(registrySnap, { requirePublished: true }) ?? getBundledExploreTemplate(templateId)
    if (!registry) throw new Error('Template is no longer available.')
    if (registry.category !== 'Checklists') throw new Error('Only checklist templates can be copied to the Visionboard.')

    const title = `${registry.title} **`
    copiedId = checklistRef.id
    transaction.set(checklistRef, {
      ...createStripboardRecordDefaults({ boardStatus: 'TODO', priority: 3 }),
      workspaceId,
      parentContainerId: boardRootId(workspaceId, 'VISIONBOARD'),
      board: 'VISIONBOARD',
      title,
      description: buildChecklistTemplateDescription(registry),
      completionCriteria: buildChecklistTemplateCriteria(registry),
      templateSourceId: registry.id,
      templateSourceVersion: registry.registryVersion,
      templateCopiedAt: serverTimestamp(),
      sourceChecklistId: checklistRef.id,
      isCompleted: false,
      weightage: 1,
      position: { x: 0, y: 0 },
      prerequisites: [],
      blockedByExternal: null,
      isHelperVisible: true,
      workflowStatus: 'CREATED',
      complexityStars: 3,
      urgencyStars: 3,
      importanceStars: 3,
      estimatedHours: null,
      actualCost: null,
      currencyCode: 'USD',
      scheduleMode: 'DATED',
      createdById: actor.id,
      createdByName: actor.name ?? 'Workspace member',
      createdAt: serverTimestamp(),
      assigneeId: null,
      assigneeName: null,
      updatedAt: serverTimestamp(),
      lastUpdatedBy: actor.id,
      lastUpdatedByName: actor.name ?? null,
    })
    stepResult.applyWrites()
    logAuditEvent(db, transaction, workspaceSnap.data(), {
      workspaceId,
      actorId: actor.id,
      actorName: actor.name ?? null,
      action: 'CHECKLIST_TEMPLATE_COPIED',
      activityType: 'CREATE',
      nodeType: 'CHECKLIST',
      targetId: checklistRef.id,
      targetTitle: title,
      summary: `Copied checklist template to Visionboard: ${registry.title}`,
      payload: {
        category: registry.category,
        sourceTemplateId: registry.id,
        registryVersion: registry.registryVersion,
        board: 'VISIONBOARD',
      },
    })
  })
  // Bootstrap the freshly-copied checklist onto the INDIVIDUAL book so the
  // layout picks a real empty slot instead of leaving it at the (0,0)
  // fallback that would stack it on top of any other template-copy already
  // sitting there. Best-effort — bootstrapChecklistsOnBoard never throws.
  if (copiedId) {
    await bootstrapChecklistsOnBoard(
      workspaceId,
      [{ id: copiedId, checklistType: 'INDIVIDUAL' }],
      { uid: actor.id, displayName: actor.name ?? null },
    )
  }
  return copiedId
}

export const deleteLocalTemplate = async (
  workspaceId: string,
  templateId: string,
  actor: TemplateImportActor,
  db: Firestore = firestoreDb,
) => {
  if (!workspaceId || !templateId || !actor.id) return
  await runTransaction(db, async (transaction) => {
    const workspaceRef = doc(db, 'workspaces', workspaceId)
    const savedRef = doc(db, 'workspaces', workspaceId, SAVED_TEMPLATES_COLLECTION, templateId)
    const cloneRef = doc(db, 'workspaces', workspaceId, WORKSPACE_TEMPLATES_COLLECTION, templateId)
    const [workspaceSnap, savedSnap, cloneSnap] = await Promise.all([
      transaction.get(workspaceRef),
      transaction.get(savedRef),
      transaction.get(cloneRef),
    ])
    const bundled = getBundledExploreTemplate(templateId)
    const title = (cloneSnap.exists() ? cloneSnap.data().title : savedSnap.data()?.title) ?? bundled?.title ?? templateId
    if (savedSnap.exists()) transaction.delete(savedRef)
    if (cloneSnap.exists()) transaction.delete(cloneRef)
    logAuditEvent(db, transaction, workspaceSnap.data(), {
      workspaceId,
      actorId: actor.id,
      actorName: actor.name ?? null,
      action: 'TEMPLATE_LOCAL_COPY_DELETED',
      activityType: 'DELETE',
      nodeType: 'TEMPLATE',
      targetId: templateId,
      targetTitle: String(title),
      summary: `Removed local template copy: ${title}`,
      payload: { templateId },
    })
  })
}

export const checkIfSaved = (savedTemplates: SavedTemplateRecord[], templateId: string) =>
  savedTemplates.some((template) => template.templateId === templateId || template.id === templateId)
